import { Min, Scale, Subtract, Total } from "@pdfking/math"

import type { CalendarArea, CalendarBoxes } from "#internal"

import { Slice } from "../layout/Slice"
import { Area } from "./Area"
import { ShiftMonth } from "./ShiftMonth"

// The share of the page a picture takes: the left of a landscape page, the
// top of any other, a tall one's least
const PICTURE_WIDTH = 0.4
const PICTURE_HEIGHT = 0.55
const TALL_PICTURE_HEIGHT = 0.35
// The small months' column, of what is beside it
const SMALL_WIDTH = 0.25

/** A calendar's page of `month`, laid out for any page: under its
 * `picture`, run off the page's other edges — or on its own when the
 * picture faces it —, its name over its days, the contact at its foot. A
 * landscape page has the picture on the left; a tall one its days down a
 * list; where the grid leaves room beside it, the months either side are
 * small in a column left of it. */
export const CalendarMonthAreas = (
  { body, contact, gap, language, page, short, year }: CalendarBoxes,
  month: number,
  picture: boolean
): CalendarArea[] => {
  const landscape = page.width > page.height
  const tall = page.height > Scale(page.width, 2)
  // The picture, and what is left of the body beside or under it
  const shown = !picture
    ? undefined
    : landscape
      ? Slice.left(page, Scale(page.width, PICTURE_WIDTH))[0]
      : Slice.top(page, Scale(page.height, tall ? TALL_PICTURE_HEIGHT : PICTURE_HEIGHT))[0]
  const rest = !shown
    ? body
    : landscape
      ? Slice.after(body, shown.width, gap)
      : Slice.under(body, shown.y, gap)
  const title_height = Min(Scale(rest.height, 0.1), Scale(short, 0.07))
  const below = Subtract(rest.height, Total(title_height, gap))
  // The months either side, small, in a column left of the month's grid
  const small = !tall && Scale(rest.width, 0.6) > below
  const [column, main] = small
    ? Slice.left(rest, Scale(rest.width, SMALL_WIDTH), gap)
    : [undefined, rest]
  const [title, grid] = Slice.top(main, title_height, gap)
  const either = column
    ? Slice.grid({ ...column, height: below }, 1, 2, gap).map((box, i) => {
        const shifted = ShiftMonth(year, month, i ? 1 : -1)
        return Area.grid(box, shifted.month, shifted.year, { title: "month_year" })
      })
    : []
  return [
    ...(shown ? [Area.picture(shown, month)] : []),
    Area.title(title, month, language),
    tall ? Area.days({ ...rest, height: below }, month, year) : Area.grid(grid, month, year),
    ...either,
    Area.footer({ ...contact, width: Subtract(Total(body.x, body.width), rest.x), x: rest.x }),
  ]
}
